/* global Theme */
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** How long the tile says "Added" before the quantity control is back */
const ADDED_CONFIRMATION_DURATION = 1800;

/** How long the live region keeps its text */
const ANNOUNCEMENT_DURATION = 5000;

/** How long we wait for the cart view to show the new line and the quantity control */
const RENDER_TIMEOUT = 3000;

/**
 * Giftbox picker inside the "Inpakken als cadeau?" cart section (PIG-320).
 * The tiles are rendered by snippets/bbd-giftbox-picker.liquid and re-rendered on every cart refresh, this element
 * only handles the clicks (add, change quantity), the arrows and the loading state.
 */
class BbdGiftboxPicker extends HTMLElement {
  #busy = false;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // scroll does not bubble, listen in the capture phase to also catch the row after a morph
    this.addEventListener('scroll', this.#updateArrows, { capture: true, passive: true });

    // A cart refresh morphs the server markup back in, including the arrows' hidden state
    this.#observer = new MutationObserver(() => this.#updateArrows());
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#resizeObserver = new ResizeObserver(() => this.#updateArrows());
    this.#resizeObserver.observe(this);

    this.#updateArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scroll', this.#updateArrows, { capture: true });
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('[data-tiles-arrow]');
    if (arrow instanceof HTMLElement) {
      this.#scroll(arrow.dataset.tilesArrow === 'next' ? 1 : -1);
      return;
    }

    const add = event.target.closest('[data-giftbox-add]');
    if (add instanceof HTMLElement && add.dataset.variantId) {
      this.#add(Number(add.dataset.variantId));
      return;
    }

    const change = event.target.closest('[data-giftbox-change]');
    if (change instanceof HTMLElement && change.dataset.lineKey) {
      this.#change(change.dataset.lineKey, Number(change.dataset.quantity));
    }
  };

  /**
   * @param {number} direction
   */
  #scroll(direction) {
    const row = this.querySelector('[data-tiles-row]');
    if (!(row instanceof HTMLElement)) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior });
  }

  #updateArrows = () => {
    const row = this.querySelector('[data-tiles-row]');
    const previous = this.querySelector('[data-tiles-arrow="previous"]');
    const next = this.querySelector('[data-tiles-arrow="next"]');
    if (!(row instanceof HTMLElement) || !(previous instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    // Only write when it changed, the observer would otherwise retrigger itself
    const hidePrevious = row.scrollLeft <= 1;
    const hideNext = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    if (previous.hidden !== hidePrevious) previous.hidden = hidePrevious;
    if (next.hidden !== hideNext) next.hidden = hideNext;
  };

  /**
   * Asks Shopify to render the cart sections in the same request, like the product form does.
   * @returns {{ sections: string, sections_url: string }}
   */
  #sectionsParams() {
    const ids = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : undefined))
      .filter(Boolean);

    return { sections: [...new Set(ids)].join(','), sections_url: window.location.pathname };
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    if (busy) this.dataset.busy = 'true';
    else delete this.dataset.busy;

    this.setAttribute('aria-busy', String(busy));
  }

  /**
   * @param {Record<string, string>} [sections]
   * @param {{ items: { key: string, variant_id: number, product_id: number }[], item_count: number }} [knownCart] - The cart of the change request, saves a request
   * @returns {Promise<{ items: { key: string, variant_id: number, product_id: number }[] }>}
   */
  async #refresh(sections, knownCart) {
    const cart = knownCart?.items
      ? knownCart
      : await (await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' })).json();

    this.dispatchEvent(
      new CartUpdateEvent(cart, this.id || 'bbd-giftbox-picker', {
        source: 'bbd-giftbox-picker',
        itemCount: cart.item_count,
        sections
      })
    );

    return cart;
  }

  /**
   * Waits until the cart view shows what the callback finds, the cart section is morphed after the update event.
   * @template T
   * @param {() => T | null | undefined | false} find
   * @returns {Promise<T | null>}
   */
  async #waitFor(find) {
    const start = Date.now();
    while (Date.now() - start < RENDER_TIMEOUT) {
      const found = find();
      if (found) return found;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return null;
  }

  /**
   * After an add the customer must see that it worked, also on a phone where the new cart line is out of view:
   * the tile says "Added" for a moment and the cart scrolls to the new line when it is not in view.
   * @param {number} variantId
   * @param {{ items: { key: string, variant_id: number, product_id: number }[] }} cart
   */
  async #confirmAdd(variantId, cart) {
    const line = cart.items.find((item) => item.variant_id === variantId);
    if (!line) return;

    const tile = await this.#waitFor(() => {
      const element = this.querySelector(`[data-giftbox-tile][data-product-id="${line.product_id}"]`);
      return element?.querySelector('.bbd-giftbox-quantity') ? element : null;
    });
    if (!tile) return;

    const added = tile.querySelector('[data-giftbox-added]');
    const quantity = tile.querySelector('.bbd-giftbox-quantity');
    if (added instanceof HTMLElement && quantity instanceof HTMLElement) {
      added.hidden = false;
      quantity.hidden = true;
      setTimeout(() => {
        added.hidden = true;
        quantity.hidden = false;
      }, ADDED_CONFIRMATION_DURATION);

      const name = tile.querySelector('.bbd-cart-tile__name')?.textContent?.trim() ?? '';
      this.#announce(`${name} ${added.textContent?.trim() ?? ''}`.trim());
    }

    const row = await this.#waitFor(() => document.querySelector(`tr[data-key="${CSS.escape(line.key)}"]`));
    if (row) this.#scrollIntoViewIfHidden(row);
  }

  /**
   * Says the text to screen readers through the live region of the picker, and empties it again afterwards.
   * @param {string} text
   */
  #announce(text) {
    const live = this.querySelector('[data-giftbox-live]');
    if (!(live instanceof HTMLElement)) return;

    live.textContent = text;
    setTimeout(() => {
      live.textContent = '';
    }, ANNOUNCEMENT_DURATION);
  }

  /**
   * Scrolls the cart to the element when it is not fully in view, the sticky header and footer of the drawer
   * do not count as view. The nearest scrollable parent is scrolled by hand: with several lines in the cart
   * scrollIntoView left the new line behind the header on a phone.
   * @param {Element} element
   */
  #scrollIntoViewIfHidden(element) {
    const drawer = element.closest('cart-drawer-component');
    const top = drawer?.querySelector('.cart-drawer__header')?.getBoundingClientRect().bottom ?? 0;
    const bottom = drawer?.querySelector('.cart-drawer__footer')?.getBoundingClientRect().top ?? window.innerHeight;
    const { top: elementTop, bottom: elementBottom } = element.getBoundingClientRect();
    if (elementTop >= top && elementBottom <= bottom) return;

    let parent = element.parentElement;
    // .cart-drawer__items is overflow auto too, but it does not scroll itself, so skip parents without overflow
    while (
      parent &&
      !(/(auto|scroll)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight + 1)
    ) {
      parent = parent.parentElement;
    }
    if (!parent) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const parentTop = parent.getBoundingClientRect().top;
    // Put the line just below the header, or the top of the scrollable area when the header is outside of it
    const offset = Math.max(top, parentTop);
    // The newest line is the first one: go all the way up so the header and the banners above it are in view too
    const isFirstLine = element.closest('cart-items-component')?.querySelector('tr[data-key]') === element;
    parent.scrollTo({ top: isFirstLine ? 0 : parent.scrollTop + elementTop - offset, behavior });
  }

  /**
   * @param {number} variantId
   */
  async #add(variantId) {
    if (this.#busy) return;
    this.#setBusy(true);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], ...this.#sectionsParams() })
        })
      );
      const result = await response.json();
      if (result.status) throw new Error(result.description ?? result.message);

      const cart = await this.#refresh(result.sections);
      this.#setBusy(false);
      await this.#confirmAdd(variantId, cart);
    } catch (error) {
      console.error('BBD giftbox picker could not add the giftbox:', error);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity - 0 removes the line
   */
  async #change(lineKey, quantity) {
    if (this.#busy) return;
    this.#setBusy(true);

    try {
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({ id: lineKey, quantity: Math.max(0, quantity), ...this.#sectionsParams() })
        })
      );
      const result = await response.json();
      if (result.errors) throw new Error(result.errors);

      await this.#refresh(result.sections, result);
    } catch (error) {
      console.error('BBD giftbox picker could not change the quantity:', error);
    } finally {
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('bbd-giftbox-picker')) {
  customElements.define('bbd-giftbox-picker', BbdGiftboxPicker);
}
